import type { IBatchStore } from './batch-store-types';
import logger from '../../utils/logger';
import { timerKey } from '../../utils/timer-manager/timer-manager';
import { DeviceStatus } from '../../constants/DeviceStatus';
import { toNumber } from '../../utils/numbers';
import {
  OUTPUT_TTL,
  OUTPUT_SHORT_TTL,
  POLLING_INTERVAL,
  POLLING_INTERVAL_SLOW,
} from '../../constants/Pac';
import {
  DoubleGatesOpenMode,
  SecondGateOpenDelayUnit,
} from '../../constants/DeviceModels';
import {
  convertInputs,
  convertOutputs,
} from '../../utils/pac-data-converter/pac-data-converter';
import { waitFor } from '../../utils/async';
import { beltSwitch } from './device-switches/belt-switch';
import moment from 'moment';
import { reaction } from 'mobx';
import type { IWeighedDataToBatch } from '../../../types/batch/DataToBatch';
import type { IBatchData } from '../../containers/Batch/IBatchData';
import type { MixDesignItem } from '../../../types/batch/MixDesignItem';
import convertUnit from '../../utils/unit-converter';
import type {
  IBelt,
  IDevice,
  IManualPanel,
  IScale,
} from '../../../types/device/DeviceModels';
import { isFeatureEnabled } from '../../utils/feature-flags';
import type { IMaterial } from '../../../types/material/MaterialModels';
import { aeratorSwitch } from './device-switches/aerator-switch';

const FEATURE_AUTO_TUNING = isFeatureEnabled('auto_tuning');

export const pacScaleMixin = {

  jogModeDecum(
    this: IBatchStore,
    data: IWeighedDataToBatch,
    initialData: IBatchData,
    scaleRef: any,
    onFinish: (data?: IWeighedDataToBatch, batchData?: IBatchData) => void = () => {},
  ) {

    const { scale, binForDecum, material, mft } = data;
    const sid = `${scale.id}-${material.code}`;
    logger.log('jog mode decum', sid, mft);
    const materialDetails = this.materialStore.materialDetails[material.materialDetailId];
    const scalePorts = this.getScalePorts(scale, material);
    const { openPort, closePort, statusPort, openPortAdditional, closePortAdditional } = scalePorts;
    let message = '';

    this.timers.clear(timerKey.device(scale.id));

    if (this.isAbort || !this.isActiveDrop(initialData)) {
      return;
    }

    let s = this.getDeviceStatus(sid);
    if (s.status === DeviceStatus.FINISH) {
      return;
    }

    const aeratorId = scale.aeratorId || binForDecum?.aeratorId;
    if (aeratorId) {
      const aerator = this.devicesStore.getDeviceById(aeratorId);
      if (aerator?.turnOffDuringJogging) {
        aeratorSwitch(this.rootStore, aeratorId, false);
      }
    }

    if (this.isHoldFeed || initialData.isHoldLoad || this.isHoldFeedDevice(scale.id) || (!scale.parentScaleId && this.isHoldDischarge) || (scaleRef.dischargeIsStop && !scaleRef.dischargeIsResume)) {
      if (this.isScaleOpen(scale, statusPort)) {
        this.closeScale(scale, openPort, closePort);
        if (this.isDoubleGateScale(scale)) {
          this.closeScale(scale, openPortAdditional, closePortAdditional);
        }
      }

      if (s.status !== DeviceStatus.HOLD && s.status !== DeviceStatus.FINISH) {
        this.deviceStatus.set(sid, {
          ...s,
          holdWeight: this.getScaleWeight(scale) - s.startWeight,
          status: DeviceStatus.HOLD,
        });
        this.setTargetsForDecumScale(data, initialData, DeviceStatus.HOLD);

        this.resetWeight(scale.inputPNo);
      }

      this.timers.set(timerKey.jog(scale.id), () => {
        this.jogModeDecum(data, initialData, scaleRef, onFinish);
      }, 1000);

      return;
    }

    if (s.status !== DeviceStatus.OVER) {
      this.deviceStatus.set(sid, {
        ...s,
        status: DeviceStatus.SETTLING,
        error: false,
      });
    }

    this.getScaleWeightAfterSettlingTime(scale, materialDetails.scaleSettlingTime, weight => {
      const tolerance = this.getTolerance(materialDetails, mft);
      let finishWeight = s.startWeight - weight;
      s = this.getDeviceStatus(sid);

      if (s.status === DeviceStatus.FINISH || this.isAbort || !this.isActiveDrop(initialData)) {
        return;
      }

      if (this.checkIndicatorValueError(weight) === DeviceStatus.INDICATOR_ERROR) {
        logger.log('indicator error', scale.id, scale.description);
        return;
      }

      if (this.isHoldFeed || initialData.isHoldLoad || this.isHoldFeedDevice(scale.id) || (!scale.parentScaleId && this.isHoldDischarge) || (scaleRef.dischargeIsStop && !scaleRef.dischargeIsResume)) {
        if (s.status !== DeviceStatus.HOLD) {
          const holdWeight = weight - s.startWeight;
          this.deviceStatus.set(sid, {
            ...s,
            holdWeight,
            status: DeviceStatus.HOLD,
          });
          this.setTargetsForDecumScale(data, initialData, DeviceStatus.HOLD);

          this.resetWeight(scale.inputPNo);
        }

        return this.jogModeDecum(data, initialData, scaleRef, onFinish);
      }

      logger.log('jog mode', material.code, weight, s.finishTarget, tolerance?.over);

      if (finishWeight < s.target - tolerance?.under) {
        this.deviceStatus.set(sid, {
          ...s,
          status: DeviceStatus.JOG,
          weight: finishWeight,
        });

        return this.openJogDecum(data, initialData, scaleRef, onFinish);

      } else if (finishWeight > s.target + tolerance?.over && !s.overridden) {

        this.deviceStatus.set(sid, {
          ...s,
          status: DeviceStatus.OVER,
          weight: finishWeight,
          error: true,
        });

        const materialCode = this.materialStore.getMaterialName(material);
        const unit = scale.unitId ? this.getUnitById(scale.unitId) : materialDetails.itemBatchUnitSymbol;
        message = `${materialCode} current weight is over target! Press Override button [F2]`;
        this.showError({ key: 'Current weight is over target! Press Override button [F2]', props: { material: materialCode }});
        logger.warn(`${message}, ${finishWeight} > ${s.target} + ${tolerance?.over}, `, weight, s.finishTarget, tolerance?.over, this.isOverride);
        this.showOverTolerancePopup({
          material: materialCode,
          deviceId: sid,
          weight: finishWeight,
          target: s.target,
          unit,
          uid: initialData.uid,
          currentDrop: initialData.currentDrop,
        });

        this.closeScale(scale, openPort, closePort);
        if (this.isDoubleGateScale(scale)) {
          this.closeScale(scale, openPortAdditional, closePortAdditional);
        }

        this.timers.set(timerKey.jog(scale.id), () => {
          this.jogModeDecum(data, initialData, scaleRef, onFinish);
        }, 1000);
        return;

      }

      logger.log('JOG mode decum FINISH -> next material for the same scale');

      this.closeScale(scale, openPort, closePort);
      if (this.isDoubleGateScale(scale)) {
        this.closeScale(scale, openPortAdditional, closePortAdditional);
      }

      if (s.error) {
        this.isOverride = false;
      }

      this.deviceStatus.set(sid, {
        ...s,
        weight: finishWeight,
        finishWeight,
        status: DeviceStatus.FINISH,
        error: false,
      });

      this.deviceStatus.set(scale.id, {
        ...this.getDeviceStatus(scale.id),
        status: DeviceStatus.FINISH,
      });

      if (Array.isArray(initialData.mixDesign)) {
        const scaleUnit = this.getUnitById(scale.unitId);

        initialData.mixDesign.forEach((v: MixDesignItem, i: number) => {
          if ((v.batchCode && v.batchCode === material.batchCode) || v.constituentItemId === material.itemId) {
            let actualWeight = finishWeight;
            let targetPerDrop = s.target;

            if (v.unitSymbol !== scaleUnit) {
              v.scaleUnit = scaleUnit;
              v.scaleWeight = weight;
              actualWeight = toNumber(convertUnit(actualWeight, scaleUnit, v.unitSymbol), 2);
              targetPerDrop = toNumber(convertUnit(s.target, scaleUnit, v.unitSymbol), 2);
            }

            v.actualWeight = actualWeight;
            v.actualPerDrop = actualWeight;
            v.targetPerDrop = targetPerDrop;

            logger.log('actual weight decum:', material.code, actualWeight, v, v.unitSymbol, scaleUnit);
            this.saveResultForDrop(initialData, v);

          }
        });
      } else if (!this.isDemo) {
        const payload = {
          quantity: finishWeight,
          deviceId: scale.id,
          deviceDescription: scale.description,
          itemId: material.itemId,
          itemCode: material.code,
          unitId: scale.unitId,
          unitSymbol: this.getUnitById(scale.unitId),
        };
        logger.log('simple ticket:', payload);
        this.ticketStore.createSimpleTicket(payload);
      }

      const quantity = toNumber(convertUnit(finishWeight, this.getUnitById(scale.unitId), material.itemInventoryUnitSymbol), 4);
      logger.log('update material', material.code, quantity, material.itemInventoryUnitSymbol);

      if (this.canReduceInventory && quantity > 0) {
        this.materialStore.updateMaterialInventoryUse(material, {
          quantity,
          comment: '',
          ticket: initialData.tId,
          isAutomatic: !initialData.materialId,
          binIds: material.bins?.filter(v => v.isPrimary).map(v => v.binId),
        });
      }

      onFinish(data, initialData);
    });

  },

  openJogDecum(this: IBatchStore, data: IWeighedDataToBatch, initialData: IBatchData, scaleRef: any, onFinish: () => void) {

    const { material, materialDetails, scale, binForDecum, mft } = data;
    const scalePorts = this.getScalePorts(scale, material);
    const { openPort, closePort, openPortAdditional, closePortAdditional } = scalePorts;

    if (this.isAbort || !this.isActiveDrop(initialData)) {
      return;
    }

    if (this.isHoldFeed || initialData.isHoldLoad || this.isHoldFeedDevice(scale.id)) {
      this.timers.set(timerKey.jog(scale.id), () => this.openJogDecum(data, initialData, scaleRef, onFinish), 2000);
      return;
    }

    const currentWeight = this.getScaleWeight(scale);
    const bin = this.devicesStore.getDeviceById(binForDecum?.id) || binForDecum;
    let jogOpenTime = this.getInitialJogOpenTime(bin, initialData);
    const s = this.getDeviceStatus(`${scale.id}-${material.code}`);

    if (bin.aeratorId) {
      const aerator = this.devicesStore.getDeviceById(bin.aeratorId);
      if (aerator?.turnOffDuringJogging) {
        aeratorSwitch(this.rootStore, bin.aeratorId, false);
      }
    }

    if (bin.autoJog) {
      const convertedWeight = toNumber(convertUnit(mft - s.weight, this.getUnitById(scale.unitId), materialDetails.itemBatchUnitSymbol), 2);
      if (FEATURE_AUTO_TUNING && bin.autoTuning && bin.jogTableAuto) {
        jogOpenTime = this.getJogOpenTime(convertedWeight, bin.jogTableAuto) || jogOpenTime;
      } else {
        jogOpenTime = this.getJogOpenTime(convertedWeight, bin.jogTable) || jogOpenTime;
      }
      logger.log('auto jog open time', jogOpenTime);
    }

    this.deviceStatus.set(`${scale.id}-${material.code}`, {
      ...s,
      status: DeviceStatus.JOG,
    });

    this.openScale(scale, `${jogOpenTime * 1000}`, openPort, closePort);
    if (this.isDoubleGateScale(scale)) {
      this.openScale(scale, `${jogOpenTime * 1000}`, openPortAdditional, closePortAdditional);
    }

    this.timers.set(timerKey.jog(scale.id), () => {
      this.closeJogDecum(data, initialData, scaleRef, jogOpenTime, currentWeight, onFinish);
    }, jogOpenTime * 1000);
  },

  closeJogDecum(
    this: IBatchStore,
    data: IWeighedDataToBatch,
    initialData: IBatchData,
    scaleRef: any,
    jogOpenTime: number,
    startJogWeight: number,
    onFinish: () => void,
  ) {

    const { material, materialDetails, scale, binForDecum } = data;
    const scalePorts = this.getScalePorts(scale, material);
    const { openPort, closePort, openPortAdditional, closePortAdditional } = scalePorts;
    const settlingTime = materialDetails.scaleSettlingTime || 1;
    const bin = this.devicesStore.getDeviceById(binForDecum.id) || binForDecum;
    let jogCloseTime = this.getJogCloseTime(bin, initialData);
    logger.log('JOG close:', material.code, scale.description, jogCloseTime);

    this.closeScale(scale, openPort, closePort, `${jogCloseTime * 1000}`);
    if (this.isDoubleGateScale(scale)) {
      this.closeScale(scale, openPortAdditional, closePortAdditional);
    }

    if (this.isAbort || !this.isActiveDrop(initialData)) {
      return;
    }

    const s = this.getDeviceStatus(`${scale.id}-${material.code}`);

    this.deviceStatus.set(`${scale.id}-${material.code}`, {
      ...s,
      status: DeviceStatus.SETTLING,
    });

    this.timers.set(timerKey.jog(scale.id), () => {

      const finishWeight = this.getScaleWeight(scale);

      if (!this.isDemo && scale.autoJog && jogOpenTime) {
        const { jogStore, weatherStore } = this.rootStore;
        const weight = finishWeight - startJogWeight;
        logger.log('jog history', material.code, jogOpenTime, weight);

        jogStore.saveJogHistory(bin.id, {
          materialDetailId: materialDetails.id,
          deviceId: bin.id,
          openPeriod: jogOpenTime,
          weight,
          moisture: materialDetails.moistureAmount || 0,
          closeDate: moment().tz(this.timezone).format(),
          temperature: weatherStore.temperature || 0,
          humidity: weatherStore.humidity || 0,
          pressure: weatherStore.pressure || 0,
        });

        jogStore.updateJogTable(bin, jogOpenTime, weight);
      }

      this.jogModeDecum(data, initialData, scaleRef, onFinish);

    }, (jogCloseTime + settlingTime) * 1000);
  },

  isIndicatorValueError(this: IBatchStore, weight: number): boolean {
    return Number(weight) === 16777216;
  },

  checkIndicatorValueError(this: IBatchStore, weight: number): string {

    if (this.isIndicatorValueError(weight)) {
      this.saveLog(`n-error Indicator error, ${weight}`);

      this.indicatorValueError = true;
      this.showError('Scale indicator value error!');
      return DeviceStatus.INDICATOR_ERROR;
    }
    this.indicatorValueError = false;

    return '';
  },

  checkIndicatorError(this: IBatchStore, ticks: string) {

    this.timers.clear(timerKey.indicatorError());

    this.timers.set(timerKey.indicatorError(), () => {
      if (this.pacConnected && this.canEmit && !this.isDemo && this.ticks && Number(this.ticks) >= 0 && Number(this.ticks) === Number(ticks)) {
        this.indicatorError = true;
        this.showError('Scale indicator error!');
      } else {
        this.indicatorError = false;
      }

      this.checkIndicatorError(this.isDemo ? null : this.ticks);
    }, 5000);
  },

  checkBeltsInterlock(this: IBatchStore) {

    reaction(
      () => this.inputs,
      () => {
        if (!this.canEmit || this.replayMode || this.maintenanceMode) {
          return;
        }

        this.devicesStore.belts.forEach((belt: IBelt) => {
          if (belt.interlockStatusPNo) {
            if (this.isBeltOn(belt.id) && this.isInputPortOff(belt.interlockStatusPNo)) {
              logger.log('belt off - interlock', belt.id, belt.description);
              beltSwitch(this.rootStore, belt.id, false, true);
            }
          }
        });
      },
    );
  },

  checkManualPanel(this: IBatchStore) {

    reaction(
      () => this.inputs,
      () => {
        if (!this.canEmit || this.replayMode || this.maintenanceMode) {
          return;
        }

        this.setManualLoad();
      },
    );
  },

  setManualLoad(this: IBatchStore) {

    this.devicesStore.manualPanels.forEach((manualPanel: IManualPanel) => {
      if (
        this.running.length
        && manualPanel.startDischPNo
        && !this.isInputPortOff(manualPanel.startDischPNo)
      ) {
        this.running[0].manualLoad = true;

        logger.log(`Manual panel: ${manualPanel.description}
            input: ${manualPanel.startDischPNo} is On - Manual Load`);
      }
    });
  },

  checkPacConnected(this: IBatchStore) {

    const start = async () => {
      await this.getStatus();
      const waitTime = this.rootStore.printStore.getPrintWindowOpenTime() ? 2000 : 300;
      await waitFor(waitTime);

      if (this.pacConnected || this.isDemo) {
        return Promise.resolve();
      }

      return Promise.reject();
    };

    return start();
  },

  checkPacConnection(this: IBatchStore) {

    this.timers.clear(timerKey.pac());
    this.timers.set(timerKey.pac(), () => {
      if (this.pac.isWSOpen() && this.canEmit && !this.isDemo && !this.replayMode && !this.isTestMode) {
        if (this.rootStore.printStore.getPrintWindowOpenTime() < 10) {
          this.showError('Plant disconnected!');
          this.pacConnected = false;
        }
      }
      this.checkPacConnection();
    }, 10000, 'Check pac connection');
  },

  getStatus(this: IBatchStore) {
    const batchStore = this;

    if (!this.isDemo && this.rootStore.pacStore.isWSOpen()) {
      this.pac.runWS('get-status');
    }

    if (this.isDemo) {
      this.pacSimulator.runWS('get-status').then((result: any) => this.onProxyData(batchStore, result));
    }

    if (this.isTestMode) {
      this.pacSimulator.runWS('get-status').then((result: any) => this.onProxyDataTestMode(batchStore, result));
    }
  },

  onProxyDisconnect(this: IBatchStore) {
    if (this.canEmit && this.pacConnected) {
      this.showError('Plant disconnected!');
    }
    this.pacConnected = false;
  },

  convertInputsAndOutputs(this: IBatchStore, data: any) {
    //new pac will send inputs and outputs as 32bit integer numbers, it must be converted to the old string format

    data = {
      ...data,
      inputs: data.inputs.includes(',') ? convertInputs(data.inputs) : data.inputs,
      outputs: data.outputs.includes(',') ? convertOutputs(data.outputs) : data.outputs,
    };

    return data;
  },

  onProxyDataTestMode(batchStore: IBatchStore, result: any) {
    if (batchStore.replayMode || !result.inputs) {
      return;
    }

    result = batchStore.convertInputsAndOutputs(result);

    const outputs = result.outputs
      .replaceAll('.', '')
      .trim()
      .split('');

    if ([...batchStore.outputs].join('') !== [...outputs].join('')) {
      batchStore.outputs = outputs;
    }

    const weights = result.weights.trim().split(' ');
    const weightsStatus = weights.shift(); // shifting first 16 leading zeros

    if ([...batchStore.weightsStatus].join('') !== [weightsStatus].join('')) {
      batchStore.weightsStatus = weightsStatus;
    }

    if ([...batchStore.channels].join('') !== [...weights].join('')) {
      batchStore.channels = weights;
    }

    const meters = result.meters.trim().split(' ');
    const metersStatus = meters.shift(); // shifting first leading zeros

    if ([...batchStore.metersStatus].join('') !== [...metersStatus].join('')) {
      batchStore.metersStatus = metersStatus;
    }

    if ([...batchStore.meters].join('') !== [...meters].join('')) {
      batchStore.meters = meters;
    }

  },

  onProxyData(batchStore: IBatchStore, result: any) {
    if (batchStore.replayMode || !result.inputs) {
      return;
    }

    const now = Date.now();
    const minTimeInMs = 180;
    // Prevent proxy data from being processed too quickly, which can cause incorrect flow rate calculations.
    if (batchStore.statusTime && now - batchStore.statusTime < minTimeInMs) {
      logger.debug(`data from proxy received too fast: ${now - batchStore.statusTime}ms < ${minTimeInMs}ms`);
      return;
    }

    const { connectionStore } = batchStore.rootStore;
    const loads = batchStore.getNumberOfLoadsAndQueue();

    result = batchStore.convertInputsAndOutputs(result);

    const outputs = result.outputs
      .replaceAll('.', '')
      .trim()
      .split('');

    const inputs = result.inputs
      .replaceAll('.', '')
      .trim()
      .split('');

    if (!batchStore.isTestMode) {
      if ([...batchStore.outputs].join('') !== [...outputs].join('')) {
        batchStore.checkContamination(outputs, batchStore.outputs);
        batchStore.outputs = outputs;
        batchStore.context.onIOChange(inputs, outputs, batchStore.channels, loads);
      }
    }

    if ([...batchStore.inputs].join('') !== [...inputs].join('')) {
      batchStore.inputs = inputs;
      batchStore.context.onIOChange(inputs, outputs, batchStore.channels, loads);
    }

    if (!batchStore.isTestMode) {
      if (result.weights) {
        const weights = result.weights.trim().split(' ');
        const weightsStatus = weights.shift(); // shifting first 16 leading zeros

        if ([...batchStore.weightsStatus].join('') !== [...weightsStatus].join('')) {
          batchStore.weightsStatus = weightsStatus;
        }

        if ([...batchStore.channels].join('') !== [...weights].join('')) {
          batchStore.channels = weights;
          batchStore.context.onIOChange(inputs, outputs, batchStore.channels, loads);
        }
      }

      if (result.meters) {
        const meters = result.meters.trim().split(' ');
        const metersStatus = meters.shift(); // shifting first leading zeros

        if ([...batchStore.metersStatus].join('') !== [...metersStatus].join('')) {
          batchStore.metersStatus = metersStatus;
        }

        if ([...batchStore.meters].join('') !== [...meters].join('')) {
          batchStore.meters = meters;
        }
      }
    }

    if (result['analog-inputs']) {
      const analogInputs = result['analog-inputs'].trim().split(' ');
      if ([...batchStore.analogInputs].join('') !== [...analogInputs].join('')) {
        batchStore.analogInputs = analogInputs;
      }
    }

    if (result['analog-outputs']) {
      const analogOutputs = result['analog-outputs'].trim().split(' ');
      if ([...batchStore.analogOutputs].join('') !== [...analogOutputs].join('')) {
        batchStore.analogOutputs = analogOutputs;
      }
    }

    if (!batchStore.isDemo && batchStore.pacVersion && batchStore.isPacUidValid(batchStore.pacVersion.uid)) {
      batchStore.pacConnected = true;
    }

    batchStore.statusCount += 1;
    batchStore.statusTime = Date.now();

    if (batchStore.canEmit && (batchStore.pacConnected || batchStore.canDemoBatch)) {
      batchStore.pacStatus = result;

      if (
        !batchStore.isTestMode
        && (
          (connectionStore.isOnline && !connectionStore.isSlow)
          || (connectionStore.isSlow && batchStore.ticks !== result.ticks && batchStore.ticks && batchStore.ticks%3 === 0)
        )
      ) {
        batchStore.emitBatchData();
      }
    }

    batchStore.ticks = result.ticks;
  },

  start(this: IBatchStore) {

    this.isRunning = true;
    const interval = this.pacConnected || this.isDemo ? POLLING_INTERVAL : POLLING_INTERVAL_SLOW;

    this.timers.clear(timerKey.getStatus());

    this.getStatus();

    this.timers.set(timerKey.getStatus(), () => {
      this.start();
    }, interval);
  },

  stop(this: IBatchStore) {

    this.isRunning = false;
    this.timers.clear(timerKey.getStatus());
  },

  getScaleWeight(this: IBatchStore, scale: IScale) {

    if (!scale.inputPNo) {
      return 0;
    }

    return toNumber(this.channels[scale.inputPNo - 1]);
  },

  isInputDisabled(this: IBatchStore, input: number): boolean {
    return input < 0;
  },

  isScaleOpen(this: IBatchStore, scale: IScale, gateStatusPNo: number): boolean {
    const statusPort = gateStatusPNo || scale.gateOneStatusPNo;
    return this.isInputDisabled(statusPort) ? false : this.inputs[statusPort - 1] === '0';
  },

  isDoubleGateScale(this: IBatchStore, scale: IScale): boolean {
    if (scale.decumulative) {
      return !!scale.doubleGatesOpenMode && (!!scale.openGateOneAdditionalPNo || !!scale.openGateTwoAdditionalPNo);
    }
    return !!scale.doubleGatesOpenMode && !!scale.openGateTwoPNo;
  },

  onScaleClose(this: IBatchStore, scale: IScale, data: IBatchData, onClose: () => void = () => {}) {

    if (!this.isScaleOpen(scale) || this.isTestMode) {
      onClose();
      return;
    }

    reaction(
      () => this.statusCount,
      (counter, reaction) => {
        if (this.isAbort || !this.isActiveDrop(data)) {
          reaction.dispose();
        }

        if (!this.isScaleOpen(scale)) {
          onClose();
          reaction.dispose();
        }
      },
    );
  },

  openScaleWithMode(this: IBatchStore, scale: IScale, openTime: string|number = OUTPUT_TTL) {

    this.timers.clear(timerKey.secondGateOpen(scale.id));
    const secondGateOpenDelay = scale.secondGateOpenDelayUnit === SecondGateOpenDelayUnit.Second
      ? (scale.secondGateOpenDelay || 0) * 1000
      : 0;

    let firstGateInitialOpenSent = false;
    let secondGateInitialOpenSent = false;

    switch(scale.doubleGatesOpenMode) {
      case DoubleGatesOpenMode.OpenGatesTogether:
        this.openScale(scale, openTime);
        this.openScale(scale, openTime, scale.openGateTwoPNo, scale.closeGateTwoPNo);
        firstGateInitialOpenSent = true;
        secondGateInitialOpenSent = true;

        break;
      case DoubleGatesOpenMode.OpenGateOneFirst:
        this.openScale(scale, openTime);
        firstGateInitialOpenSent = true;

        if (scale.secondGateOpenDelayUnit === SecondGateOpenDelayUnit.Second) {
          logger.log(`second gate ${scale.description} will be open in ${secondGateOpenDelay/1000} sec`);
          this.timers.set(timerKey.secondGateOpen(scale.id), () => {
            this.openScale(scale, openTime, scale.openGateTwoPNo, scale.closeGateTwoPNo);
            logger.log(`open second gate ${scale.description} after ${secondGateOpenDelay/1000} sec`);

            this.deviceStatus.set(scale.id, {
              ...this.getDeviceStatus(scale.id),
              secondGateInitialOpenSent: true,
            });

          }, secondGateOpenDelay);
        }

        break;
      case DoubleGatesOpenMode.OpenGateTwoFirst:
        this.openScale(scale, openTime, scale.openGateTwoPNo, scale.closeGateTwoPNo);
        secondGateInitialOpenSent = true;

        if (scale.secondGateOpenDelayUnit === SecondGateOpenDelayUnit.Second) {
          logger.log(`second (first) gate ${scale.description} will be open in ${secondGateOpenDelay/1000} sec`);
          this.timers.set(timerKey.secondGateOpen(scale.id), () => {
            this.openScale(scale, openTime);
            logger.log(`open second (first) gate ${scale.description} after ${secondGateOpenDelay/1000} sec`);

            this.deviceStatus.set(scale.id, {
              ...this.getDeviceStatus(scale.id),
              firstGateInitialOpenSent: true,
            });

          }, secondGateOpenDelay);
        }

        break;
      default:
        this.openScale(scale, openTime);
        firstGateInitialOpenSent = true;
    }

    this.deviceStatus.set(scale.id, {
      ...this.getDeviceStatus(scale.id),
      firstGateInitialOpenSent,
      secondGateInitialOpenSent,
    });
  },

  openScale(this: IBatchStore, scale: IScale, openTime: string|number = OUTPUT_TTL, openPort: string, closePort: string) {

    const o = [{
      port: openPort || scale.openGateOnePNo,
      value: openTime,
    }];

    if (closePort
      || (!scale.decumulative && scale.closeGateOnePNo && !scale.switchGateTypePNo)
      || (!scale.decumulative && scale.closeGateOnePNo && scale.switchGateTypePNo && this.isScaleSwitchGatePortOn(scale))
    ) {
      o.push({ port: closePort || scale.closeGateOnePNo, value: '0' });
    }

    const key = openPort
      ? timerKey.custom('close-scale', scale.id, openPort)
      : timerKey.custom('close-scale', scale.id);
    this.timers.clear(key);

    this.setOutputs(o);
  },

  closeScale(this: IBatchStore, scale: IScale, openPort: string, closePort: string, time: number|string = OUTPUT_SHORT_TTL) {

    const { configuration } = this.settingsStore;
    const closeTime = time && time !== OUTPUT_SHORT_TTL ? time : (configuration?.holdScalesClosed ? OUTPUT_TTL : OUTPUT_SHORT_TTL);
    this.setOutput(openPort || scale.openGateOnePNo, '0');

    if (closePort
      || (!scale.decumulative && scale.closeGateOnePNo && !scale.switchGateTypePNo)
      || (!scale.decumulative && scale.closeGateOnePNo && scale.switchGateTypePNo && this.isScaleSwitchGatePortOn(scale))
    ) {
      this.setOutput(closePort || scale.closeGateOnePNo, closeTime);
    }

    const key = openPort
      ? timerKey.custom('close-scale', scale.id, openPort)
      : timerKey.custom('close-scale', scale.id);

    this.timers.set(key, () => {
      if (!scale.decumulative && this.isScaleOpen(scale)) {
        this.closeScale(scale, openPort, closePort, closeTime);
      }
    }, 3000, `Close scale #${scale.id}${openPort ? ` port ${openPort}` : ''}`);
  },

  isDecumScaleOpen(this: IBatchStore, scale: IScale, showWarning: boolean = false, forceClose: boolean = false): boolean {

    if (!scale.decumulative) {
      return false;
    }

    const scaleId = scale.id;
    const materials = this.materialStore.materials.filter((v: IMaterial) => v.scaleId === scaleId && v.visible);
    let isOpen = false;

    materials.forEach((material: IMaterial) => {
      if (scaleId && scaleId === material.scaleId) {
        const scalePorts = this.getScalePorts(scale, material);
        const sid = `${scaleId}-${material.code}`;
        const s = this.getDeviceStatus(sid);

        if (this.isScaleOpen(scale, scalePorts.statusPort) || this.isInputDisabled(scalePorts.statusPort)) {

          if (showWarning && !this.isInputDisabled(scalePorts.statusPort)) {
            isOpen = true;

            this.deviceStatus.set(sid, {
              ...s,
              status: DeviceStatus.OPEN_ERROR,
              error: true,
            });
          }

          if (forceClose) {
            logger.log(`Decum scale ${sid} is open or input disabled ${scalePorts.statusPort}!, closing ...`);
            this.closeScale(scale, scalePorts.openPort, scalePorts.closePort);
          }

        } else if (s.status === DeviceStatus.OPEN_ERROR) {
          this.deviceStatus.set(sid, {
            ...s,
            status: DeviceStatus.IDLE,
            error: false,
          });
        }
      }
    });

    return isOpen;
  },

  closeDecumScale(this: IBatchStore, scale: IScale) {

    this.isDecumScaleOpen(scale, false, true);
  },

  onDeviceStatus(
    this: IBatchStore,
    device: IDevice,
    status: string[] = [DeviceStatus.IDLE],
    onResolve: (device: IDevice) => void = () => {},
  ) {

    const deviceStatus = this.getDeviceStatus(device.id).status;
    const isStatusOK = (deviceStatus: string) => status.includes(deviceStatus);

    if (isStatusOK(deviceStatus)) {
      onResolve(device);
      return;
    }

    reaction(
      () => Array.from(this.deviceStatus.values()).find(v => v.id === device.id),
      (data, reaction) => {
        if (this.isAbort) {
          reaction.dispose();
          return;
        }

        if (data && isStatusOK(data.status)) {
          reaction.dispose();
          onResolve(device);
        }
      },
    );
  },

};
